import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Check } from "lucide-react";
import useLanguage from "@/hooks/useLanguage";

/*
  Header language selector. Config-driven (enabled locales only), keyboard
  operable (arrows, Home/End, Enter, type-ahead, Escape), closes on outside
  click, returns focus to the trigger on close. Square corners, no shadow.
*/
export default function LanguageSwitcher() {
  const { t } = useTranslation();
  const { locales, current, selectLocale } = useLanguage();
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const optionRefs = useRef([]);

  const currentIndex = Math.max(
    0,
    locales.findIndex((l) => l.code === current.code)
  );

  useEffect(() => {
    if (open) setActiveIdx(currentIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (open && optionRefs.current[activeIdx]) {
      optionRefs.current[activeIdx].focus();
    }
  }, [open, activeIdx]);

  useEffect(() => {
    function onDocClick(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const closeToTrigger = () => {
    setOpen(false);
    if (triggerRef.current) triggerRef.current.focus();
  };

  const choose = (code) => {
    setOpen(false);
    selectLocale(code);
  };

  const onKeyDown = (e) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      closeToTrigger();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % locales.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => (i - 1 + locales.length) % locales.length);
    } else if (e.key === "Home") {
      e.preventDefault();
      setActiveIdx(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setActiveIdx(locales.length - 1);
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      choose(locales[activeIdx].code);
    } else if (e.key.length === 1) {
      const ch = e.key.toLowerCase();
      const idx = locales.findIndex((l) => l.name.toLowerCase().startsWith(ch));
      if (idx >= 0) setActiveIdx(idx);
    }
  };

  return (
    <div
      className="lang-switcher"
      ref={rootRef}
      onKeyDown={onKeyDown}
      data-testid="lang-switcher"
    >
      <button
        ref={triggerRef}
        type="button"
        className="lang-switcher__trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t("common.langSwitcher.aria")}
        onClick={() => setOpen((o) => !o)}
        data-testid="lang-switcher-trigger"
      >
        <span
          className={`fi fi-${current.flag.toLowerCase()} switcher-flag`}
          aria-hidden="true"
        />
        <span className="lang-switcher__code">{current.code.toUpperCase()}</span>
        <ChevronDown
          size={16}
          strokeWidth={1.5}
          aria-hidden="true"
          className="lang-switcher__chevron"
        />
      </button>

      {open && (
        <ul
          className="lang-switcher__panel"
          role="listbox"
          aria-label={t("common.langSwitcher.aria")}
          tabIndex={-1}
          data-testid="lang-switcher-panel"
        >
          {locales.map((l, i) => {
            const active = l.code === current.code;
            return (
              <li
                key={l.code}
                role="option"
                aria-selected={active}
                ref={(el) => (optionRefs.current[i] = el)}
                tabIndex={-1}
                className={`lang-switcher__option${
                  active ? " lang-switcher__option--active" : ""
                }`}
                onClick={() => choose(l.code)}
                data-testid={`lang-option-${l.code}`}
              >
                <span
                  className={`fi fi-${l.flag.toLowerCase()} switcher-flag`}
                  aria-hidden="true"
                />
                <span className="lang-switcher__name">{l.name}</span>
                {active && (
                  <Check
                    size={16}
                    strokeWidth={1.5}
                    className="lang-switcher__check"
                    aria-hidden="true"
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
